import React, { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Grid, Card, TextField, IconButton, Box, Typography, Paper, Snackbar, Alert, Backdrop, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";


// Icons for media
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from '@mui/icons-material/Description';
import { compressImage } from "utils/imageCompression";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  content: Yup.string()
    .required("Content is required"),
  photos: Yup.array(),
  video_url: Yup.array().of(Yup.string().url("Please enter a valid URL")),
  pdfs: Yup.array(),
});

function LessonsAdd() {
  const navigate = useNavigate();
  const { lesson_id } = useParams();
  const isEdit = Boolean(lesson_id);
  const [contentLength, setContentLength] = useState(0);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingPdfs, setExistingPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [videoInput, setVideoInput] = useState("");

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const viewDocument = (file) => {
    const isWord = file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx');

    if (isWord) {
      // Convert Word document to HTML using mammoth
      const reader = new FileReader();
      reader.onload = function (event) {
        const arrayBuffer = event.target.result;
        mammoth.convertToHtml({ arrayBuffer: arrayBuffer })
          .then(result => {
            setViewDocHtml(result.value);
            setIsViewingWord(true);
            setViewDocUrl(true); // Just to open the dialog
          })
          .catch(err => {
            console.error('Error converting Word document:', err);
            setSnackbar({ open: true, message: 'Failed to preview Word document', severity: 'error' });
          });
      };
      reader.readAsArrayBuffer(file);
    } else {
      // For PDFs, use blob URL
      const url = URL.createObjectURL(file);
      setViewDocUrl(url);
      setIsViewingWord(false);
      setViewDocHtml(null);
    }
  };

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data;
  };

  const {
    data: meData,
  } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      content: "",
      photos: [],
      video_url: [],
      pdfs: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!meData?.user_id) {
        setSnackbar({ open: true, message: "User authentication failed.", severity: "error" });
        return;
      }

      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("title", values.title);
        formData.append("content", values.content);
        formData.append("user_id", meData.user_id);
        formData.append("status", 2);
        formData.append("video_url", values.video_url.join(","));

        const compressedPhotos = await Promise.all(
          values.photos.map(file => compressImage(file))
        );

        compressedPhotos.forEach((file) => formData.append("photos", file));
        values.pdfs.forEach((file) => formData.append("pdfs", file));

        if (isEdit) {
          formData.append("existing_photos", JSON.stringify(existingPhotos));
          formData.append("existing_pdfs", JSON.stringify(existingPdfs));
        }

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/lessons/edit/${lesson_id}/${meData.user_id}`
          : `${process.env.REACT_APP_API_URL}/lessons/post`;

        const response = await axios({
          method: isEdit ? 'put' : 'post',
          url: url,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        setSnackbar({
          open: true,
          message: response?.data?.message || `Lesson ${isEdit ? 'updated' : 'posted'} successfully!`,
          severity: "success"
        });

        setTimeout(() => {
          navigate("/lessons/list");
        }, 2000);
      } catch (error) {
        console.error("Error saving lesson:", error);
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || "Failed to save lesson. Please try again.",
          severity: "error"
        });
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch lesson details for editing
  const fetchLessonDetails = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/lessons/details/${lesson_id}`, { withCredentials: true });
    return response.data;
  };

  const { data: lessonResponse } = useQuery({
    queryKey: ["lessonDetails", lesson_id],
    queryFn: fetchLessonDetails,
    enabled: isEdit,
  });

  useEffect(() => {
    if (isEdit && lessonResponse?.data) {
      const lesson = lessonResponse.data;
      formik.setValues({
        title: lesson.title || "",
        content: lesson.content || "",
        photos: [],
        video_url: (() => {
          if (!lesson.videos) return [];
          if (Array.isArray(lesson.videos)) return lesson.videos;
          try {
            // Check if it's a JSON array string
            const parsed = JSON.parse(lesson.videos);
            if (Array.isArray(parsed)) return parsed;
          } catch (e) {
            // Not a JSON array, fall back to comma split
          }
          return lesson.videos.split(",").map(u => u.trim()).filter(u => u !== "");
        })(),
        pdfs: [],
      });
      setExistingPhotos(lesson.photos || []);
      setExistingPdfs(lesson.pdfs || []);
      // Accurate visible text count
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = lesson.content || "";
      const text = tempDiv.textContent || tempDiv.innerText || "";
      // Handle HTML entities like &nbsp; correctly
      const visibleText = text.replace(/\u00a0/g, ' ');
      setContentLength(visibleText.trim().length);
    }
  }, [lessonResponse, isEdit]);

  // Debugging: Log state changes
  React.useEffect(() => {
    // console.log("Form State Update - meData:", meData?.user_id, "Errors:", formik.errors);
  }, [meData, formik.errors, formik.values]);

  const handleEditorChange = (value) => {
    formik.setFieldValue('content', value);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = value || "";
    const text = tempDiv.textContent || tempDiv.innerText || "";
    const visibleText = text.replace(/\u00a0/g, ' ');
    setContentLength(visibleText.trim().length);

    // Ensure the field is marked as touched
    if (!formik.touched.content) {
      formik.setFieldTouched('content', true, false);
    }
  };

  const handleFileChange = (event, type) => {
    const files = Array.from(event.target.files);

    const MAX_SIZE = 25 * 1024 * 1024; // 25MB
    // ALLOWED_TYPES for documents: PDF, Word (.doc, .docx)
    const ALLOWED_TYPES = type === 'photos'
      ? ["image/jpeg", "image/png", "image/jpg"]
      : ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

    for (const file of files) {
      if (file.size > MAX_SIZE) {
        setSnackbar({ open: true, message: `File too large: ${file.name}. Max size is 25MB.`, severity: "error" });
        event.target.value = "";
        return;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        setSnackbar({ open: true, message: `Invalid file type: ${file.name}.`, severity: "error" });
        event.target.value = "";
        return;
      }
    }

    if (files.length > 0) {
      if (type === 'photos') {
        formik.setFieldValue("photos", [...formik.values.photos, ...files]);
      } else if (type === 'pdfs') {
        formik.setFieldValue("pdfs", [...formik.values.pdfs, ...files]);
      }
    }
    event.target.value = "";
  };

  const removeFile = (fileDetails, type) => {
    if (type === 'photos') {
      formik.setFieldValue("photos", formik.values.photos.filter(f => f !== fileDetails));
    } else if (type === 'pdfs') {
      formik.setFieldValue("pdfs", formik.values.pdfs.filter(f => f !== fileDetails));
    }
  }

  const handleAddVideoUrl = () => {
    if (!videoInput.trim()) return;

    // Simple URL validation
    try {
      new URL(videoInput);
    } catch (e) {
      setSnackbar({ open: true, message: "Please enter a valid URL", severity: "error" });
      return;
    }

    formik.setFieldValue("video_url", [...formik.values.video_url, videoInput]);
    setVideoInput("");
  };

  const removeVideoUrl = (index) => {
    const newUrls = [...formik.values.video_url];
    newUrls.splice(index, 1);
    formik.setFieldValue("video_url", newUrls);
  };

  const removeExistingFile = (url, type) => {
    if (type === 'photos') setExistingPhotos(prev => prev.filter(p => p !== url));
    else if (type === 'pdfs') setExistingPdfs(prev => prev.filter(p => p !== url));
  }

  const handleCancel = () => {
    if (isEdit) {
      navigate("/lessons/list");
    } else {
      formik.resetForm();
      setExistingPhotos([]);
      setExistingPdfs([]);
      setContentLength(0);
      setSnackbar({
        open: true,
        message: "Form cleared.",
        severity: "info"
      });
    }
  };

  // Debugging: Log errors whenever they change
  React.useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      // console.log("Current Formik Errors:", formik.errors);
    }
  }, [formik.errors]);

  const handlePostLesson = (e) => {
    e.preventDefault();
    // console.log("Post Lesson button clicked");
    // console.log("Current formik.values:", formik.values);
    // console.log("Current formik.errors:", formik.errors);

    // Manually mark all fields as touched to show validation errors
    formik.setTouched({
      title: true,
      content: true,
      photos: true,
      pdfs: true
    });

    if (Object.keys(formik.errors).length > 0) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors before submitting.",
        severity: "error"
      });
    }

    formik.handleSubmit(e);
  };

  // Styles
  const navyBlue = "#1a237e";

  return (
    <DashboardLayout>
      <MDBox py={2}>
        <Card sx={{ overflow: 'visible', width: '100%' }}>
          <MDBox p={3}>
            <MDBox mb={2} textAlign="center">
              <MDTypography variant="h4" fontWeight="bold" color="dark">
                {isEdit ? "Update Your Lesson" : "Post Your Lessons"}
              </MDTypography>
              <MDTypography variant="button" color="text">
                {isEdit
                  ? "Refine your experience and keep the community updated."
                  : "Share your field experiences, wins, and learnings with everyone."}
              </MDTypography>
            </MDBox>
            <form onSubmit={formik.handleSubmit}>
              {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
                <MDBox mb={2}>
                  <Alert severity="error" variant="filled" sx={{ color: "white !important" }}>
                    <Typography variant="caption" color="white" fontWeight="bold">
                      Please correct the following: {Object.values(formik.errors).join(", ")}
                    </Typography>
                  </Alert>
                </MDBox>
              )}
              <MDBox mb={3}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  variant="standard"
                  InputProps={{
                    style: { fontSize: '1.5rem', fontWeight: 600, color: navyBlue, paddingBottom: 10 }
                  }}
                  InputLabelProps={{
                    style: { fontSize: '1.1rem', color: navyBlue }
                  }}
                />
              </MDBox>

              <MDBox mb={3}>
                <Box sx={{
                  mt: 1,
                  '& .quill': { borderRadius: 2, border: formik.errors.content ? '1px solid red' : 'none' },
                  '& .ql-container': { minHeight: 250, fontSize: '1rem', color: navyBlue },
                  '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
                  '& .ql-container.ql-snow': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }
                }}>
                  <ReactQuill
                    theme="snow"
                    value={formik.values.content}
                    onChange={handleEditorChange}
                    placeholder="Content (Required)..."
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['link'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['clean']
                      ]
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <MDTypography variant="caption" color={contentLength > 2000 ? "error" : "text"} fontWeight="medium">
                    {Math.max(0, 2000 - contentLength)} characters remaining
                  </MDTypography>
                </Box>
                {formik.touched.content && formik.errors.content && (
                  <MDTypography variant="caption" color="error">{formik.errors.content}</MDTypography>
                )}
              </MDBox>

              <MDBox mb={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Media Uploads (Optional)
                </MDTypography>
                {/* Upload Buttons Area */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box
                      border="2px dashed #4caf50"
                      p={5}
                      borderRadius={3}
                      textAlign="center"
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: '#e8f5e9',
                        transition: '0.3s',
                        '&:hover': { backgroundColor: '#c8e6c9' }
                      }}
                      onClick={() => document.getElementById('photo-upload').click()}
                    >
                      <PhotoLibraryIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                      <MDTypography variant="button" display="block" mt={1} fontWeight="bold" color="success">
                        PHOTOS
                      </MDTypography>
                      <MDTypography variant="caption" color="text">Max 25MB per file</MDTypography>
                      <input id="photo-upload" type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => handleFileChange(e, 'photos')} />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      border="2px dashed #f44336"
                      p={5}
                      borderRadius={3}
                      textAlign="center"
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: '#ffebee',
                        transition: '0.3s',
                        '&:hover': { backgroundColor: '#ffcdd2' }
                      }}
                      onClick={() => document.getElementById('pdf-upload').click()}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 40, color: '#f44336' }} />
                      <MDTypography variant="button" display="block" mt={1} fontWeight="bold" color="error">
                        DOCUMENTS
                      </MDTypography>
                      <MDTypography variant="caption" color="text">PDF, DOC, DOCX</MDTypography>
                      <input id="pdf-upload" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple style={{ display: 'none' }} onChange={(e) => handleFileChange(e, 'pdfs')} />
                    </Box>
                  </Grid>
                </Grid>

                {/* Video Link Field */}
                <MDBox mt={3}>
                  <MDBox mb={2} p={2} sx={{ backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                    <MDTypography variant="body2" color="info" fontWeight="medium">
                      <strong>Note:</strong> Direct video uploads are not supported. Please provide Google Drive links (with &quot;Anyone with the link can view&quot; permission) or other video URLs for your submission.
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap={1} mb={2}>
                    <TextField
                      fullWidth
                      label="Video Link (Paste URL)"
                      value={videoInput}
                      onChange={(e) => setVideoInput(e.target.value)}
                      variant="standard"
                    />
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleAddVideoUrl}
                      disabled={!videoInput.trim()}
                    >
                      Add Video
                    </MDButton>
                  </MDBox>

                  {formik.values.video_url && formik.values.video_url.length > 0 && (
                    <MDBox mb={2} p={2} sx={{ border: "1px solid #ddd", borderRadius: 1, backgroundColor: "#f9f9f9" }}>
                      <MDTypography variant="h6" fontWeight="bold" mb={1} sx={{ fontSize: "14px" }}>
                        Added Video Links:
                      </MDTypography>
                      <MDBox display="flex" flexWrap="wrap" gap={1.5}>
                        {formik.values.video_url.map((url, index) => {
                          const lowerUrl = url.toLowerCase();
                          let IconComp = LinkIcon;
                          let iColor = "info";

                          if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
                            IconComp = YouTubeIcon;
                            iColor = "error";
                          } else if (lowerUrl.includes("drive.google.com")) {
                            IconComp = InsertDriveFileIcon;
                            iColor = "info";
                          } else if (lowerUrl.match(/\.(mp4|mov|quicktime)$/)) {
                            IconComp = PlayCircleIcon;
                            iColor = "info";
                          }

                          return (
                            <MDBox
                              key={index}
                              position="relative"
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 1,
                                border: "1px solid #ccc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "white",
                                cursor: "pointer"
                              }}
                              onClick={() => window.open(url, '_blank')}
                            >
                              <IconComp color={iColor} sx={{ fontSize: 32 }} />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeVideoUrl(index);
                                }}
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  backgroundColor: "error.main",
                                  color: "white",
                                  padding: "2px",
                                  "&:hover": { backgroundColor: "error.dark" }
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </MDBox>
                          );
                        })}
                      </MDBox>
                    </MDBox>
                  )}
                </MDBox>

                {/* Photos Previews */}
                {(existingPhotos.length > 0 || formik.values.photos.length > 0) && (
                  <Box mt={3}>
                    <MDTypography variant="h6" fontWeight="bold" mb={1} color="success">
                      Photos
                    </MDTypography>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      {existingPhotos.map((url, index) => (
                        <Box key={`existing-${index}`} position="relative" sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', boxShadow: 3, border: '2px solid #4caf50' }}>
                          <img src={url} alt="existing" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Box sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.9)', borderBottomLeftRadius: 8 }}>
                            <IconButton onClick={() => removeExistingFile(url, 'photos')} size="small" sx={{ color: 'red', p: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      {formik.values.photos.map((file, index) => (
                        <Box key={`new-${index}`} position="relative" sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
                          <img src={URL.createObjectURL(file)} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Box sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.8)', borderBottomLeftRadius: 8 }}>
                            <IconButton onClick={() => removeFile(file, 'photos')} size="small" sx={{ color: 'red', p: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* PDFs/Docs Previews */}
                {(existingPdfs.length > 0 || formik.values.pdfs.length > 0) && (
                  <Box mt={3}>
                    <MDTypography variant="h6" fontWeight="bold" mb={1} color="error">
                      Documents
                    </MDTypography>
                    <Grid container spacing={2}>
                      {existingPdfs.map((url, index) => {
                        const isWord = url.toLowerCase().endsWith('.doc') || url.toLowerCase().endsWith('.docx');
                        const previewUrl = isWord
                          ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
                          : url;

                        return (
                          <Grid item xs={12} md={4} key={`existing-p-${index}`}>
                            <Box p={2} border="1px solid #f44336" borderRadius={2} bgcolor="#fff5f5" height="100%">
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="caption" fontWeight="bold">Existing Doc {index + 1}</Typography>
                                <IconButton onClick={() => removeExistingFile(url, 'pdfs')} size="small" sx={{ color: 'grey', '&:hover': { color: 'red' } }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <iframe
                                src={previewUrl}
                                title="Doc Preview"
                                width="100%"
                                height="150px"
                                style={{ borderRadius: 4, border: "1px solid #ccc" }}
                              />
                            </Box>
                          </Grid>
                        );
                      })}
                      {formik.values.pdfs.map((file, index) => {
                        const isWord = file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx');

                        return (
                          <Grid item xs={12} md={4} key={`new-p-${index}`}>
                            <Box p={2} border="1px solid #ddd" borderRadius={2} bgcolor="#f8f9fa" height="100%">
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }} fontWeight="bold">{file.name}</Typography>
                                <IconButton onClick={() => removeFile(file, 'pdfs')} size="small" sx={{ color: 'grey', '&:hover': { color: 'red' } }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {isWord ? (
                                <Box
                                  width="100%"
                                  height="150px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flexDirection="column"
                                  bgcolor="#fff"
                                  borderRadius={2}
                                  sx={{
                                    border: '1px solid #ccc',
                                  }}
                                >
                                  <DescriptionIcon sx={{ fontSize: 48, color: '#2196f3', mb: 1 }} />
                                  <Typography variant="caption" color="text" fontWeight="bold" textAlign="center" px={1}>
                                    Word Document
                                  </Typography>
                                  <Typography variant="caption" color="text" sx={{ fontSize: '0.65rem', mt: 0.5, textAlign: 'center', px: 1, wordBreak: 'break-word' }}>
                                    {file.name}
                                  </Typography>
                                </Box>
                              ) : (
                                <iframe src={URL.createObjectURL(file)} title="PDF Preview" width="100%" height="150px" style={{ borderRadius: 4, border: "1px solid #ccc" }} />
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

              </MDBox>

              <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="gradient"
                  color="error"
                  onClick={handleCancel}
                  sx={{ px: 4 }}
                >
                  Cancel
                </MDButton>
                <MDButton
                  type="submit"
                  disabled={loading}
                  variant="gradient"
                  sx={{
                    px: 4,
                    background: 'linear-gradient(195deg, #0b6750, #0b6750)',
                    color: '#ffffff',
                    '&:hover': {
                      background: 'linear-gradient(195deg, #094d3d, #094d3d)',
                    }
                  }}
                >
                  {isEdit ? <><SaveIcon sx={{ mr: 1 }} /> Update Lesson</> : <><SendIcon sx={{ mr: 1 }} /> Post Lesson</>}
                </MDButton>
              </MDBox>
            </form>
          </MDBox>
        </Card>
      </MDBox>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default LessonsAdd;
